<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Home, HomeAvatar } from 'src/types/Home';
	import { setUnitName, setHomeAvatar } from 'src/services/iotu/house-management';
	import { currentUnitId, refreshHomes } from 'src/stores/account/home';
	import { toast } from '@Shelly/shelly-design-system';
	import EditNameAvatarModal from '../Avatar/EditNameAvatarModal.svelte';

	export let showModal = false;
	export let home: Home | undefined;

	let homeName = '';
	let avatar: HomeAvatar = {};
	let isSaving = false;
	let initialized = false;

	$: isCurrentHome = !!home && home.id === $currentUnitId;

	$: if (showModal && home && !initialized) {
		homeName = home.name ?? '';
		avatar = { ...(home.avatar ?? {}) };
		initialized = true;
	}
	$: if (!showModal) {
		initialized = false;
	}

	$: isPremium = home?.plan === 'premium';

	const onClose = (): void => {
		showModal = false;
	};

	const areAvatarsEqual = (a?: HomeAvatar, b?: HomeAvatar): boolean =>
		JSON.stringify({ image: a?.image, backgroundColor: a?.backgroundColor }) ===
		JSON.stringify({ image: b?.image, backgroundColor: b?.backgroundColor });

	const onSave = async (e: CustomEvent<{ name: string; avatar: HomeAvatar }>): Promise<void> => {
		if (!home) return;

		const { name, avatar: nextAvatar } = e.detail;

		const nameChanged = !!name && name !== home.name;
		const avatarChanged = isCurrentHome && !areAvatarsEqual(nextAvatar, home.avatar);

		if (!nameChanged && !avatarChanged) {
			onClose();
			return;
		}

		isSaving = true;

		try {
			let allSucceeded = true;

			if (nameChanged) {
				const unitId = Number(home.id);
				const res = await setUnitName(Number.isFinite(unitId) ? unitId : null, name);
				allSucceeded = allSucceeded && res?.isok !== false;
			}

			if (avatarChanged) {
				const res = await setHomeAvatar(nextAvatar);
				allSucceeded = allSucceeded && res?.isok !== false;
			}

			if (!allSucceeded) {
				throw new Error('save failed');
			}

			await refreshHomes();

			toast.open({
				type: 'success',
				title: $format('settings_home_updated', { default: 'Home updated successfully' }),
			});

			onClose();
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_global', { default: 'Something went wrong' }),
			});
		} finally {
			isSaving = false;
		}
	};
</script>

<EditNameAvatarModal
	{showModal}
	type="home"
	title={$format('edit_home', { default: 'Edit home' })}
	nameLabel={$format('input_label_home_name', { default: 'Home Name' })}
	nameInputId="edit-home-name-input"
	bind:name={homeName}
	bind:avatar
	{isPremium}
	allowAvatarEdit={isCurrentHome}
	loading={isSaving}
	on:save={onSave}
	on:close={onClose}
/>
